/**
 * Where a signup came from. Every page load that carries attribution (campaign tags, click
 * ids, a referral code, a landing-site CTA's landing_path, or an outside referrer) is a
 * touch: the first one this browser saw is kept, the latest one replaces the last.
 * /auth/register and /auth/google send both; the backend writes them once, on the new
 * account (app/services/auth/signup_touch.py). Stored in localStorage so the touch survives
 * the Google sign-in round trip and a signup that happens days later.
 */

const PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'ref',
] as const

type TouchParam = (typeof PARAMS)[number]

export type SignupTouch = Partial<Record<TouchParam, string>> & {
  referrer?: string
  landing_path?: string
  captured_at?: string
}

export interface SignupTouches {
  first?: SignupTouch
  last?: SignupTouch
}

const FIRST_KEY = 'mxf_signup_first_touch'
const LAST_KEY = 'mxf_signup_last_touch'

function outsideReferrer(referrer: string, host: string): string | undefined {
  try {
    return referrer && new URL(referrer).host !== host ? referrer : undefined
  } catch {
    return undefined
  }
}

/** The touch a page load carries, or null when it carries no attribution. */
export function touchFromLocation(href: string, referrer: string, now: Date): SignupTouch | null {
  const url = new URL(href)
  const params = url.searchParams
  const touch: SignupTouch = {}
  for (const key of PARAMS) {
    const value = params.get(key)?.trim()
    if (value) touch[key] = value.slice(0, key === 'ref' ? 64 : 255)
  }
  const from = params.get('referrer') || outsideReferrer(referrer, url.host)
  if (from) touch.referrer = from.slice(0, 2000)
  const landingPath = params.get('landing_path')
  if (!Object.keys(touch).length && !landingPath) return null
  touch.landing_path = (landingPath || url.pathname).slice(0, 2000)
  touch.captured_at = now.toISOString()
  return touch
}

function read(key: string): SignupTouch | undefined {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as SignupTouch) : undefined
  } catch {
    return undefined
  }
}

function write(key: string, touch: SignupTouch): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(touch))
  } catch {
    // Storage blocked (private mode): the signup simply carries no source.
  }
}

/** Record this page load's touch. Call once at startup. */
export function captureSignupTouch(): void {
  const touch = touchFromLocation(window.location.href, document.referrer, new Date())
  if (!touch) return
  if (!read(FIRST_KEY)) write(FIRST_KEY, touch)
  write(LAST_KEY, touch)
}

/** The touches to send with a signup, or undefined when there are none. */
export function signupTouches(): SignupTouches | undefined {
  const first = read(FIRST_KEY)
  const last = read(LAST_KEY)
  return first || last ? { first, last } : undefined
}

/** Forget the touches once an account has been created with them. */
export function clearSignupTouches(): void {
  try {
    window.localStorage.removeItem(FIRST_KEY)
    window.localStorage.removeItem(LAST_KEY)
  } catch {
    // Nothing stored.
  }
}
